import React, { useMemo, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from 'recharts';
import {
  Beaker,
  Trophy,
  Layers,
  Scale,
  GitBranch,
  Database,
  Cpu,
  TrendingUp,
  Info,
} from 'lucide-react';
import results from '../data/mlResults.json';

/* ----------------------------------------------------------------------------
 * ML Study — Classical ML vs. LLM scoring for resume <-> job-description matching.
 * Reads the static study export (frontend/src/data/mlResults.json) produced by
 * ml/run_study.py on the Kaggle UpdatedResumeDataSet. No backend needed.
 * Neutral slate palette + a single signal-green accent (reserved for the leader).
 * -------------------------------------------------------------------------- */

const C = {
  ink: '#0F172A',
  slate700: '#334155',
  slate500: '#64748B',
  slate400: '#94A3B8',
  slate300: '#CBD5E1',
  hairline: '#E2E8F0',
  page: '#F8FAFC',
  surface: '#FFFFFF',
  green: '#2563EB',
  greenInk: '#1D4FD8',
  greenSoft: '#EFF4FE',
  greenLine: '#BFDBFE',
  track: '#EEF2F6',
};

const serif = "'Source Serif 4', Georgia, 'Times New Roman', serif";
const mono = 'ui-monospace, SFMono-Regular, Menlo, monospace';

const TYPE_BADGE = {
  classical: { label: 'Classical ML', bg: C.greenSoft, color: C.greenInk, border: C.greenLine },
  baseline: { label: 'Baseline', bg: '#F8FAFC', color: C.slate500, border: C.hairline },
  llm: { label: 'LLM', bg: '#F5F3FF', color: C.slate700, border: C.hairline },
};

const fmt = (n, d = 3) =>
  typeof n === 'number' && !Number.isNaN(n) ? n.toFixed(d) : '—';

/* ---------- small presentational pieces ---------- */

function Eyebrow({ children }) {
  return (
    <div
      style={{
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: C.green,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: C.green }} />
      {children}
    </div>
  );
}

function Card({ children, style }) {
  return (
    <div
      style={{
        background: C.surface,
        border: `1px solid ${C.hairline}`,
        borderRadius: 12,
        boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function TypeBadge({ type }) {
  const s = TYPE_BADGE[type] || TYPE_BADGE.baseline;
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        padding: '2px 9px',
        borderRadius: 20,
        background: s.bg,
        color: s.color,
        border: `1px solid ${s.border}`,
        whiteSpace: 'nowrap',
      }}
    >
      {s.label}
    </span>
  );
}

function Stat({ icon: Icon, value, label, accent }) {
  return (
    <Card style={{ padding: '16px 18px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <Icon size={15} style={{ color: accent ? C.green : C.slate400 }} strokeWidth={1.9} />
        <span style={{ fontSize: 12, color: C.slate500, fontWeight: 500 }}>{label}</span>
      </div>
      <div
        style={{
          fontFamily: serif,
          fontSize: 24,
          fontWeight: 600,
          color: accent ? C.greenInk : C.ink,
          lineHeight: 1,
        }}
      >
        {value}
      </div>
    </Card>
  );
}

/* ---------- custom chart tooltip ---------- */

function ChartTip({ active, payload, suffix }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div
      style={{
        background: C.surface,
        border: `1px solid ${C.hairline}`,
        borderRadius: 8,
        boxShadow: '0 4px 14px rgba(15,23,42,0.10)',
        padding: '8px 12px',
        fontSize: 12.5,
      }}
    >
      <div style={{ fontWeight: 600, color: C.ink, marginBottom: 2 }}>{p.label || p.category}</div>
      <div style={{ color: C.slate500 }}>
        {suffix}: <span style={{ fontFamily: mono, color: C.ink }}>{fmt(payload[0].value, 4)}</span>
      </div>
    </div>
  );
}

/* ---------- page ---------- */

function MLStudy() {
  const { models, fairness, dataset, config, n_pairs: nPairs, methodology, limitations } = results;

  const leader = models[0];
  const classicalCount = models.filter((m) => m.type === 'classical').length;

  // model comparison chart data (ranked, already sorted by ROC-AUC in the export)
  const aucData = useMemo(
    () =>
      models.map((m) => ({
        label: m.label,
        roc_auc: m.roc_auc,
        type: m.type,
        isLeader: m.model === leader.model,
      })),
    [models, leader.model]
  );

  // fairness chart data (per-category AUC for the leading classical model)
  const fairData = useMemo(
    () =>
      [...fairness.per_category]
        .sort((a, b) => a.roc_auc - b.roc_auc)
        .map((c) => ({ category: c.category, roc_auc: c.roc_auc })),
    [fairness.per_category]
  );

  const [activeMetric] = useState('roc_auc');

  const barColor = (d) => (d.isLeader ? C.green : d.type === 'baseline' ? C.slate300 : C.slate400);

  return (
    <div style={{ maxWidth: 1120, margin: '0 auto', color: C.ink }}>
      {/* header */}
      <div style={{ marginBottom: 26 }}>
        <Eyebrow>Research · Model study</Eyebrow>
        <h1
          style={{
            fontFamily: serif,
            fontSize: 30,
            fontWeight: 600,
            letterSpacing: '-0.01em',
            margin: '12px 0 8px',
          }}
        >
          Classical ML vs. LLM scoring for résumé&nbsp;↔&nbsp;job-description matching
        </h1>
        <p style={{ fontSize: 14.5, color: C.slate500, lineHeight: 1.6, margin: 0, maxWidth: 760 }}>
          The screening core of AutoHire, framed as a supervised relevance task: given a
          (résumé, job-description) pair, predict match vs. no-match. We compare five classical
          classifiers against unsupervised baselines on {nPairs.toLocaleString()} labelled pairs from the{' '}
          <span style={{ color: C.slate700, fontWeight: 600 }}>{dataset.name}</span> ({dataset.n_resumes}{' '}
          résumés, {dataset.n_categories_total} job categories), with {config.folds}-fold
          cross-validation.
        </p>
      </div>

      {/* headline stats */}
      <div
        style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 14, marginBottom: 22 }}
        className="ml-stat-grid"
      >
        <Stat icon={Trophy} accent value={`${leader.label}`} label="Top model" />
        <Stat icon={TrendingUp} accent value={fmt(leader.roc_auc, 4)} label="Best ROC-AUC" />
        <Stat icon={Layers} value={nPairs.toLocaleString()} label="Labelled pairs" />
        <Stat icon={Scale} value={fmt(fairness.disparity, 3)} label="Fairness disparity (top model)" />
      </div>

      {/* model comparison TABLE */}
      <Card style={{ overflow: 'hidden', marginBottom: 22 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 22px',
            borderBottom: `1px solid ${C.hairline}`,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <Cpu size={16} style={{ color: C.slate500 }} />
            <h3 style={{ fontFamily: serif, fontSize: 16, fontWeight: 600, margin: 0 }}>
              Model comparison
            </h3>
          </div>
          <span style={{ fontSize: 12.5, color: C.slate500 }}>
            ranked by ROC-AUC · {classicalCount} classical + {models.length - classicalCount} baselines
          </span>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5, minWidth: 720 }}>
            <thead>
              <tr style={{ background: C.page }}>
                {['#', 'Model', '', 'ROC-AUC', 'F1', 'Precision', 'Recall', 'Brier', 'Threshold'].map(
                  (h, i) => (
                    <th
                      key={i}
                      style={{
                        textAlign: i <= 2 ? 'left' : 'right',
                        padding: '11px 16px',
                        fontWeight: 600,
                        fontSize: 11.5,
                        letterSpacing: '0.04em',
                        textTransform: 'uppercase',
                        color: C.slate500,
                        borderBottom: `1px solid ${C.hairline}`,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {h}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {models.map((m, i) => {
                const isLeader = m.model === leader.model;
                return (
                  <tr
                    key={m.model}
                    style={{
                      background: isLeader ? C.greenSoft : 'transparent',
                      borderBottom: `1px solid ${C.hairline}`,
                    }}
                  >
                    <td style={{ padding: '12px 16px', color: C.slate400, fontFamily: mono, fontSize: 12.5 }}>
                      {i + 1}
                    </td>
                    <td style={{ padding: '12px 16px', fontWeight: 600, color: C.ink, whiteSpace: 'nowrap' }}>
                      {isLeader && (
                        <Trophy
                          size={13}
                          style={{ color: C.green, marginRight: 7, verticalAlign: '-1px' }}
                          strokeWidth={2.2}
                        />
                      )}
                      {m.label}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <TypeBadge type={m.type} />
                    </td>
                    <td
                      style={{
                        padding: '12px 16px',
                        textAlign: 'right',
                        fontFamily: mono,
                        fontWeight: isLeader ? 700 : 500,
                        color: isLeader ? C.greenInk : C.ink,
                      }}
                    >
                      {fmt(m.roc_auc, 4)}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', fontFamily: mono, color: C.slate700 }}>
                      {fmt(m.f1, 3)}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', fontFamily: mono, color: C.slate700 }}>
                      {fmt(m.precision, 3)}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', fontFamily: mono, color: C.slate700 }}>
                      {fmt(m.recall, 3)}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', fontFamily: mono, color: C.slate700 }}>
                      {fmt(m.brier, 3)}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', fontFamily: mono, color: C.slate400 }}>
                      {fmt(m.best_threshold, 3)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div
          style={{
            padding: '11px 22px',
            fontSize: 12,
            color: C.slate400,
            borderTop: `1px solid ${C.hairline}`,
            background: C.page,
          }}
        >
          F1 reported at the best-F1 threshold; precision / recall / Brier at the 0.5 decision
          threshold. Out-of-fold predictions — no train/test leakage.
        </div>
      </Card>

      {/* charts row: ROC-AUC by model + per-category fairness */}
      <div
        style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 22, marginBottom: 22 }}
        className="ml-chart-grid"
      >
        {/* ROC-AUC by model */}
        <Card style={{ padding: '20px 20px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 4 }}>
            <TrendingUp size={16} style={{ color: C.slate500 }} />
            <h3 style={{ fontFamily: serif, fontSize: 15.5, fontWeight: 600, margin: 0 }}>
              ROC-AUC by model
            </h3>
          </div>
          <p style={{ fontSize: 12.5, color: C.slate500, margin: '0 0 14px' }}>
            Higher is better. The signal-green bar is the leading model.
          </p>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={aucData}
              layout="vertical"
              margin={{ top: 0, right: 44, bottom: 0, left: 8 }}
            >
              <CartesianGrid horizontal={false} stroke={C.hairline} />
              <XAxis
                type="number"
                domain={[0.85, 1]}
                tick={{ fontSize: 11, fill: C.slate400 }}
                tickLine={false}
                axisLine={{ stroke: C.hairline }}
              />
              <YAxis
                type="category"
                dataKey="label"
                width={132}
                tick={{ fontSize: 11.5, fill: C.slate700 }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                cursor={{ fill: 'rgba(15,23,42,0.03)' }}
                content={<ChartTip suffix="ROC-AUC" />}
              />
              <Bar dataKey={activeMetric} radius={[0, 5, 5, 0]} barSize={18} isAnimationActive>
                {aucData.map((d, i) => (
                  <Cell key={i} fill={barColor(d)} />
                ))}
                <LabelList
                  dataKey={activeMetric}
                  position="right"
                  formatter={(v) => fmt(v, 3)}
                  style={{ fontSize: 11, fill: C.slate500, fontFamily: mono }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* per-category fairness */}
        <Card style={{ padding: '20px 20px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 4 }}>
            <Scale size={16} style={{ color: C.slate500 }} />
            <h3 style={{ fontFamily: serif, fontSize: 15.5, fontWeight: 600, margin: 0 }}>
              Fairness — ROC-AUC by job family
            </h3>
          </div>
          <p style={{ fontSize: 12.5, color: C.slate500, margin: '0 0 14px' }}>
            {fairness.model_label} across {fairData.length} job families · disparity{' '}
            <span style={{ fontFamily: mono, color: C.slate700 }}>{fmt(fairness.disparity, 3)}</span>{' '}
            (max − min).
          </p>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={fairData}
              layout="vertical"
              margin={{ top: 0, right: 44, bottom: 0, left: 8 }}
            >
              <CartesianGrid horizontal={false} stroke={C.hairline} />
              <XAxis
                type="number"
                domain={[0.9, 1]}
                tick={{ fontSize: 11, fill: C.slate400 }}
                tickLine={false}
                axisLine={{ stroke: C.hairline }}
              />
              <YAxis
                type="category"
                dataKey="category"
                width={132}
                tick={{ fontSize: 11, fill: C.slate700 }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                cursor={{ fill: 'rgba(15,23,42,0.03)' }}
                content={<ChartTip suffix="ROC-AUC" />}
              />
              <Bar dataKey="roc_auc" radius={[0, 5, 5, 0]} barSize={13} isAnimationActive fill={C.green}>
                {fairData.map((d, i) => (
                  <Cell key={i} fill={d.roc_auc >= 0.99 ? C.green : '#5BB994'} />
                ))}
                <LabelList
                  dataKey="roc_auc"
                  position="right"
                  formatter={(v) => fmt(v, 3)}
                  style={{ fontSize: 10.5, fill: C.slate500, fontFamily: mono }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* methodology */}
      <Card style={{ padding: '22px 24px', marginBottom: 22 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <Beaker size={18} style={{ color: C.green }} />
          <h3 style={{ fontFamily: serif, fontSize: 17, fontWeight: 600, margin: 0 }}>Methodology</h3>
        </div>
        <p style={{ fontSize: 14, color: C.slate700, lineHeight: 1.7, margin: '0 0 18px' }}>
          {methodology}
        </p>

        {/* pipeline strip */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
            padding: '14px 16px',
            background: C.page,
            border: `1px solid ${C.hairline}`,
            borderRadius: 10,
          }}
        >
          {[
            'Clean text',
            'TF-IDF (1–2 grams)',
            '[résumé | JD | cosine]',
            'LR · DT · RF · GB · SVM',
            `${config.folds}-fold stratified CV`,
            'ROC-AUC · F1 · Brier · fairness',
          ].map((step, i, arr) => (
            <React.Fragment key={step}>
              <span
                style={{
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: i === arr.length - 1 ? C.greenInk : C.slate700,
                  background: i === arr.length - 1 ? C.greenSoft : C.surface,
                  border: `1px solid ${i === arr.length - 1 ? C.greenLine : C.hairline}`,
                  borderRadius: 7,
                  padding: '6px 11px',
                  whiteSpace: 'nowrap',
                  fontFamily: i === 2 || i === 3 ? mono : 'inherit',
                }}
              >
                {step}
              </span>
              {i < arr.length - 1 && (
                <span style={{ color: C.slate300, fontSize: 14, userSelect: 'none' }}>→</span>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* config + course mapping */}
        <div
          style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 18, marginTop: 18 }}
          className="ml-meta-grid"
        >
          <MetaItem icon={Database} label="Dataset" value={dataset.name} />
          <MetaItem icon={Layers} label="Pairs / categories" value={`${nPairs.toLocaleString()} · ${dataset.n_categories_with_jd}`} />
          <MetaItem icon={GitBranch} label="CV folds" value={`${config.folds}-fold stratified`} />
          <MetaItem icon={Cpu} label="Features" value={`TF-IDF ≤ ${config.max_features.toLocaleString()}`} />
        </div>
      </Card>

      {/* limitations / honest note */}
      <Card style={{ padding: '18px 22px', marginBottom: 32, background: C.page }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <Info size={18} style={{ color: C.slate400, flex: 'none', marginTop: 2 }} />
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 13.5 }}>Honest limitations</div>
            <p style={{ fontSize: 13.5, color: C.slate500, lineHeight: 1.6, margin: 0 }}>
              {limitations}
            </p>
            <p style={{ fontSize: 12, color: C.slate400, lineHeight: 1.6, margin: '8px 0 0' }}>
              Source: {dataset.source_url}. Reproduce with{' '}
              <code style={{ fontFamily: mono, color: C.slate500 }}>
                python3 run_study.py --no-llm --folds {config.folds} --pairs-per-category{' '}
                {config.pairs_per_category}
              </code>{' '}
              from <code style={{ fontFamily: mono, color: C.slate500 }}>ml/</code>.
            </p>
          </div>
        </div>
      </Card>

      <style>{`
        @media (max-width: 980px) {
          .ml-chart-grid { grid-template-columns: 1fr !important; }
          .ml-stat-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .ml-meta-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
        @media (max-width: 560px) {
          .ml-stat-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}

function MetaItem({ icon: Icon, label, value }) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5 }}>
        <Icon size={14} style={{ color: C.slate400 }} strokeWidth={1.9} />
        <span style={{ fontSize: 11.5, color: C.slate500, fontWeight: 500 }}>{label}</span>
      </div>
      <div style={{ fontSize: 13.5, fontWeight: 600, color: C.ink }}>{value}</div>
    </div>
  );
}

export default MLStudy;
